import 'intersection-observer';
import './theme.scss';
import 'bootstrap-daterangepicker/daterangepicker.css';
import 'tippy.js/dist/tippy.css';
import '../node_modules/react-grid-layout/css/styles.css';
import '../node_modules/react-resizable/css/styles.css';
import './App.scss';

import { BrowserRouter, Navigate, Route, RouteProps, Routes } from 'react-router-dom';

import AccidentHistory from '@/features/AccidentHistory';
import Addons from './features/Addons';
import AirlineTrips from './features/AirlineTrips';
import Airlines from './pages/Airlines';
import ApolloRoot from './components/ApolloRoot';
import Automation from '@/features/Automation';
import Availability from './pages/Availability';
import CatchError from '@/components/CatchError';
import ChangePasswordModal from './components/ChangePasswordModal';
import Clients from './pages/Clients';
import ContractedRates from './pages/ContractedRates';
import DriveTimeTool from '@/features/DriveTimeTool';
import DriveTimeToolImports from '@/features/DriveTimeTool/pages/Imports';
import DriveTimeToolRouteSelection from '@/features/DriveTimeTool/pages/RouteSelection';
import Fcrs from './pages/Fcrs';
import HtmlTripPage from '@/features/Trips/new';
import ImportHistory from './pages/Manifests/ImportHistory';
import IncidentHistory from '@/features/IncidentHistory';
import InvoiceHistory from './pages/InvoiceHistory';
import InvoicePreview from './pages/InvoicePreview';
import IssueHistory from '@/features/IssueHistory';
import Locations from './pages/Locations';
import Login from './pages/Login';
import LostAndFoundHistory from '@/features/LostAndFoundHistory';
import ManifestPreview from './pages/Manifests/ManifestPreview';
import Manifests from './features/Manifests';
import MessageCenter from '@/features/MessageCenter';
import NotFound from './pages/NotFound';
import PdfLayout from '@/features/PdfLayout';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ProtectedRoute from './components/ProtectedRoute';
import PunchPolicy from './pages/PunchPolicy';
import Rates from './pages/Rates';
import Ratings from './pages/Ratings';
import { ReactNode } from 'react';
import ReportProblem from './pages/ReportProblem';
import Reports from './pages/Reports';
import ResetPassword from './pages/ResetPassword';
import Roles from './pages/Roles';
import Sandbox from './pages/Sandbox';
import Settings from '@/features/Settings';
import { SocketProvider } from '@/hooks/useSocket/context';
import SummaryHistory from '@/features/SummaryHistory';
import { Toaster } from 'sonner';
import Training from '@/features/Settings/components/Training';
import Users from './pages/Users';
import Vehicles from './features/Vehicles';
import { pascalCase } from '@/utils';
import useSession from './state/session';

/* Routes
  Populating the routes dynamically from here keeps management of the router simple.
  The following Object is looped over, with the Key being the path of the Route, and
  the value providing the Page/Component that is to be rendered.

  The NotFound page is not included here because it has to be the last Route provided.

  UPDATE: 3/21/2024
  - Added createRoute to dynamically create routes with or without the ProtectedRoute wrapper.
  - Only the routes that have been updated to no longer provide their own wrapper have been updated to use this.
*/
const createRoute =
  (title: string, RenderRoute: (props?: RouteProps) => ReactNode, open: boolean = false) =>
  // eslint-disable-next-line react/display-name
  (props: RouteProps): ReactNode =>
    !open ? (
      <ProtectedRoute title={title} className={pascalCase(title)}>
        <RenderRoute {...props} />
      </ProtectedRoute>
    ) : (
      <RenderRoute {...props} />
    );

const routes: Record<string, (props: RouteProps) => ReactNode> = {
  // Public Routes
  '/login': createRoute('Login', Login, true),
  // Protected Routes
  // TODO: Update all routes to use createRoute.
  '/': createRoute('Trips', HtmlTripPage),
  '/availability': Availability,
  '/message-center': MessageCenter,
  '/clients': createRoute('Clients', Clients),
  '/airline-trips': createRoute('Trips', AirlineTrips),
  '/airlines': createRoute('Airlines', Airlines),
  '/contract-rates': createRoute('Contract Rates Report', ContractedRates),
  '/complaints': createRoute('Complaints', Fcrs),
  '/imports/history': ImportHistory,
  '/invoices': createRoute('Invoice History', InvoiceHistory),
  '/invoices/:id': createRoute('Invoice Preview', InvoicePreview),
  '/locations': Locations,
  '/manifests': createRoute('Manifests', Manifests),
  '/manifests/:id': createRoute('Manifest Preview', ManifestPreview),
  '/addons': createRoute('Addons', Addons),
  '/addons/:id': createRoute('Addon Preview', ManifestPreview),
  '/permissions': Roles,
  '/privacy-policy': PrivacyPolicy,
  '/punch-policy': createRoute('Punch Policy', PunchPolicy),
  '/rates': Rates,
  '/ratings': createRoute('Ratings', Ratings),
  '/report': ReportProblem,
  '/reports': Reports,
  '/reports/:type': Reports,
  '/reports/:type/:id': Reports,
  '/reset-password': ResetPassword,
  '/sandbox/:route': Sandbox,
  '/trips': createRoute('Trips', HtmlTripPage),
  '/users': Users,
  '/vehicles': createRoute('Vehicles', Vehicles),
  '/settings': createRoute('Settings', Settings),
  '/settings/:sub-route': createRoute('Settings', Settings),
  '/settings/:sub-route/:child-route': createRoute('Settings', Training),
  '/incident-history': IncidentHistory,
  '/accident-history': AccidentHistory,
  '/issue-history': IssueHistory,
  '/lost-and-found-history': LostAndFoundHistory,
  '/summary-history': SummaryHistory,
  '/tools/drive-time': DriveTimeTool,
  '/tools/drive-time/imports/:importId': DriveTimeToolRouteSelection,
  '/tools/drive-time/imports': DriveTimeToolImports,
  '/pdf/:type/:id?': PdfLayout,
  '/automation': Automation,
  '/automation/:sub-route': Automation,
};

const App = (): ReactNode => {
  const Homepage = useSession(({ state }) => routes?.[state?.config?.userConfig?.homepage] || routes['/trips']);
  return (
    <SocketProvider>
      <ApolloRoot>
        <CatchError>
          <Toaster
            expand={false}
            richColors
            position="top-right"
            closeButton
            offset={0}
            toastOptions={{
              classNames: {
                closeButton: 'Toast-Close',
                actionButton: 'Toast-Action',
              },
            }}
            duration={4000}
          />
          <BrowserRouter>
            <Routes>
              {Object.entries(routes).map(([path, Page]: [string, (props: RouteProps) => ReactNode], index: number): ReactNode => {
                if (path === '/') {
                  return <Route path="/" element={<Homepage />} key={`${'/'}_${index}`} />;
                }
                return <Route path={path} element={<Page />} key={`${path}_${index}`} />;
              })}
              <Route path={'/trips/v4'} element={<Navigate to={'/trips'} replace={true} />} />
              <Route path={'*'} element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          <div className="Drawers" />
          <ChangePasswordModal type="reset" />
        </CatchError>
      </ApolloRoot>
    </SocketProvider>
  );
};

export default App;
export { routes };
